/* eslint-disable @typescript-eslint/naming-convention */
import React, { useState, useEffect, useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import { Typography, Select, Icon, Loader } from 'foundations-library/components'
import { SmsNumberCampaignContext } from '../contexts/SmsNumberCampaignContext'
import { required } from 'validation-rules'
import { formatPhoneNumber } from 'phone-numbers'

const useStyles = makeStyles(() => ({
    title: {
        marginBottom: '40px'
    },
    instruction: {
        marginBottom: '30px'
    },
    note: {
        marginBottom: '15px'
    },
    loaderContainer: {
        display: 'flex',
        justifyContent: 'center',
        padding: '20px 0'
    },
    selectedHeader: {
        marginTop: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    selectedCount: {
        minWidth: '48px',
        height: '23px',
        borderRadius: '99px',
        padding: '2px 10px',
        background: '#000000',
        color: '#FFFFFF',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box'
    },
    chipsWrap: {
        marginTop: '20px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px'
    },
    closableChip: {
        minWidth: '148px',
        height: '28px',
        gap: '10px',
        borderRadius: '20px',
        padding: '2px 3px 2px 10px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        background: '#F4F4F4'
    },
    chipText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    chipClose: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer'
    },
    menuInfoTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        width: '100%'
    },
    menuInfoText: {
        color: '#737373'
    },
    menuInfoTextMax: {
        color: '#E68C23'
    },
    menuInfoCount: {
        minWidth: '48px',
        height: '23px',
        borderRadius: '99px',
        padding: '2px 10px',
        color: '#FFFFFF',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        flexShrink: 0
    },
    menuInfoCountDefault: {
        background: '#1A1A1A'
    },
    menuInfoCountMax: {
        background: '#E68C23'
    }
}))

const ClosableChip = ({ value, onRemove }) => {
    const classes = useStyles()

    return (
        <div className={classes.closableChip}>
            <Typography variant='subtitle2' className={classes.chipText}>
                {formatPhoneNumber(value)}
            </Typography>

            <div className={classes.chipClose} onClick={onRemove}>
                <Icon name={'cancel'} size={'medium'} color='neutral-400' />
            </div>
        </div>
    )
}

ClosableChip.propTypes = {
    value: PropTypes.string,
    onRemove: PropTypes.func
}

/**
 * @param {object} props
 */
const NumberSelectStep = (props) => {
    const classes = useStyles()

    const savedNumbers = props.passData?.number_select_step?.selectedPhoneNumber || []
    const selectedCampaignId = props.passData?.campaign_step?.selectedCampaign?.csp_campaign_id ?? null

    const { eligibleNumbers, fetchEligibleNumbers, fetchEligibleNumbersStatus } = useContext(SmsNumberCampaignContext)
    const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(savedNumbers)

    const invalid = selectedPhoneNumber.length === 0
    const maxSelectedPhoneNumber = 49
    const loadingEligibleNumbers = selectedCampaignId != null && ['idle', 'pending'].includes(fetchEligibleNumbersStatus)

    const isMaxSelected = selectedPhoneNumber.length === maxSelectedPhoneNumber

    const dropdownTitleText = isMaxSelected
        ? 'You have selected maximum numbers.'
        : 'Please select at least 1 number. Maximum allowed is 49.'

    const dropdownBadgeValue = isMaxSelected
        ? '49'
        : `${selectedPhoneNumber.length}/49`

    const selectOptions = useMemo(() => {
        const infoOption = {
            value: '__numbers-select-info__',
            title: true,
            renderComponent: (
                <div className={classes.menuInfoTitle}>
                    <Typography
                        variant='body3'
                        className={isMaxSelected ? classes.menuInfoTextMax : classes.menuInfoText}
                    >
                        {dropdownTitleText}
                    </Typography>
                    <div
                        className={`${classes.menuInfoCount} ${
                            isMaxSelected ? classes.menuInfoCountMax : classes.menuInfoCountDefault
                        }`}
                    >
                        <Typography variant='subtitle3' color='white'>
                            {dropdownBadgeValue}
                        </Typography>
                    </div>
                </div>
            )
        }

        const numberOptions = (eligibleNumbers || []).map((item) => ({
            value: item.did,
            text: formatPhoneNumber(item.did),
            selected: selectedPhoneNumber.some((selected) => selected.did === item.did),
            disabled:
                    selectedPhoneNumber.length === maxSelectedPhoneNumber &&
                    !selectedPhoneNumber.some((selected) => selected.did === item.did)
        }))

        return [infoOption, ...numberOptions]
    }, [eligibleNumbers, selectedPhoneNumber, dropdownTitleText, dropdownBadgeValue, classes.menuInfoTitle, classes.menuInfoText, classes.menuInfoCount])

    const getSaveData = () => {
        return {
            selectedPhoneNumber
        }
    }

    const phoneOnChangeHandler = (selectedOptions) => {
        const selectedValues = (selectedOptions || []).map((item) => item.value)
        const selectedNumbers = (eligibleNumbers || []).filter((item) => selectedValues.includes(item.did))
        setSelectedPhoneNumber(selectedNumbers)
    }

    const removeSelectedNumber = (did) => {
        setSelectedPhoneNumber((prev) => prev.filter((item) => item.did !== did))
    }

    useEffect(() => {
        if (props.saveId) {
            props.update({
                completed: true,
                gotoNext: true,
                saveData: getSaveData()
            })
        }
    }, [props.saveId])

    useEffect(() => {
        props.update({ disableNextButton: () => invalid })
    }, [invalid])

    useEffect(() => {
        if (selectedCampaignId == null) return
        fetchEligibleNumbers(selectedCampaignId)
    }, [fetchEligibleNumbers, selectedCampaignId])

    return (
        <div>
            <Typography
                variant='h5'
                remoteConfigID='transfer_number_number_assignment_title'
                className={classes.title}
            />

            <Typography
                variant='body1'
                className={classes.instruction}
            >
                Select the phone number(s) you want to associate with this approved campaign. Once assigned, these numbers will be authorized to send messages.
            </Typography>

            <Typography
                variant='body1'
                className={classes.note}
            >
                Selecting numbers does not automatically enable SMS. Approval is required before messaging can begin.
            </Typography>

            {loadingEligibleNumbers
                ? (
                    <div className={classes.loaderContainer} data-testid='number-select-loader'>
                        <Loader inlineBlock />
                    </div>
                )
                : (
                    <Select
                        fullWidth={true}
                        selectMultiple={true}
                        returnObject={true}
                        options={selectOptions}
                        onChange={phoneOnChangeHandler}
                        helperText='Please select at least 1 number. Maximum allowed is 49.'
                        label='Select number(s)'
                        placeholder='Add numbers to campaign'
                        rules={[required()]}
                    />
                )}

            {selectedPhoneNumber.length > 0 && (
                <>
                    <div className={classes.selectedHeader}>
                        <Typography variant='subtitle3'>
                            Selected numbers
                        </Typography>

                        <div className={classes.selectedCount}>
                            <Typography variant='subtitle3' color='white'>
                                {selectedPhoneNumber.length}/49
                            </Typography>
                        </div>
                    </div>

                    <div className={classes.chipsWrap}>
                        {selectedPhoneNumber.map((item) => (
                            <ClosableChip
                                key={item.did}
                                value={item.did}
                                onRemove={() => removeSelectedNumber(item.did)}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}

NumberSelectStep.propTypes = {
    passData: PropTypes.object,
    update: PropTypes.func,
    saveId: PropTypes.number
}

NumberSelectStep.defaultProps = {
    passData: {},
    update: () => { /* no-op */ },
    saveId: null
}

export default NumberSelectStep
